import React from 'react';
import Loadable from 'react-loadable';
import { Redirect, Switch } from 'react-router-dom';
import EditableRoute from '@components/EditableRoute';

import { AppealsRoutes, prefix } from './constants';
import PageWrapper from './components/PageWrapper';
import ConferenceWrapper from '@components/Conference/ConferenceWrapper';

const Loading = () => <div />;

const AppealsCommercialArtifacts = Loadable({
  loader: () => import('./pages/commercialArtifacts'),
  loading: Loading,
});

const AppealsCommercialComponents = Loadable({
  loader: () => import('./pages/commercialComponents'),
  loading: Loading,
});

const AppealsCommercialConfiguration = Loadable({
  loader: () => import('./pages/commercialConfiguration'),
  loading: Loading,
});

const AppealsSearch = Loadable({
  loader: () => import('./pages/appealsSearch'),
  loading: Loading,
});

const AppealSummary = Loadable({
  loader: () => import('./pages/appealSummary'),
  loading: Loading,
});

const AppealOwnerAgentPresentation = Loadable({
  loader: () =>
    import('./pages/appealSummary/components/ownerAgentPresentation'),
  loading: Loading,
});

const CreateAppealWalkthru = Loadable({
  loader: () => import('../appraisal/pages/appealCreate/walkThrough'),
  loading: Loading,
});

const MassCreateAppeal = Loadable({
  loader: () => import('../appraisal/pages/appealMassCreate'),
  loading: Loading,
});

const ResetPrimaryProtester = Loadable({
  loader: () => import('./pages/resetPrimaryProtester'),
  loading: Loading,
});

const AddAuthorizedAgentProtest = Loadable({
  loader: () => import('./pages/authorizedAgentProtest'),
  loading: Loading,
});

const InformalMeetingImport = Loadable({
  loader: () => import('./pages/informalMeetingImport'),
  loading: Loading,
});

const FormalMeetingImport = Loadable({
  loader: () => import('./pages/formalMeetingImport'),
  loading: Loading,
});

const AppealSmartLists = Loadable({
  loader: () => import('../appraisal/pages/smartListLauncher'),
  loading: Loading,
});

const DocketsSearch = Loadable({
  loader: () => import('./pages/docketsSearch'),
  loading: Loading,
});

const AppealConfigurations = Loadable({
  loader: () => import('./pages/appealConfigurations'),
  loading: Loading,
});

const EletronicDeliveryNoticeSetting = Loadable({
  loader: () => import('./pages/eletronicDeliveryNoticeSetting'),
  loading: Loading,
});

const BoardMemberMaintenance = Loadable({
  loader: () => import('./pages/boardMemberMaintenance'),
  loading: Loading,
});

const PanelMaintenance = Loadable({
  loader: () => import('./pages/panelMaintenance'),
  loading: Loading,
});

const StatusCodes = Loadable({
  loader: () => import('./pages/statusCodes'),
  loading: Loading,
});

const DocketLocations = Loadable({
  loader: () => import('./pages/docketLocations'),
  loading: Loading,
});

const Dashboard = Loadable({
  loader: () => import('@modules/appeals/pages/dashboard'),
  loading: Loading,
});

const FinalLetterHistory = Loadable({
  loader: () => import('./pages/FinalLetterHistory/FinalLetterHistoryListing'),
  loading: Loading,
});

const FinalLetterDetail = Loadable({
  loader: () => import('./pages/FinalLetterHistory/FinalLetterDetail'),
  loading: Loading,
});

const MassFinalizeHistory = Loadable({
  loader: () =>
    import('./pages/massFinalizeHistory/MassFinalizeHistoryListing'),
  loading: Loading,
});

const MassLetterHistory = Loadable({
  loader: () => import('./pages/massLetterHistory/MassLetterHistoryListing'),
  loading: Loading,
});

const MassProcessHistory = Loadable({
  loader: () => import('./pages/massProcessHistory'),
  loading: Loading,
});

const MassAttachHistory = Loadable({
  loader: () => import('./pages/massAttachHistory/MassAttachHistoryListing'),
  loading: Loading,
});

const EvidenceUploadHistory = Loadable({
  loader: () =>
    import('./pages/evidenceUploadHistory/EvidenceUploadHistoryListing'),
  loading: Loading,
});

const MassDownloadHistory = Loadable({
  loader: () =>
    import('./pages/massDownloadHistory/MassDownloadHistoryListing'),
  loading: Loading,
});

const MassAoaHistory = Loadable({
  loader: () => import('./pages/massAoaHistory/MassAoaHistoryListing'),
  loading: Loading,
});

const WaitingRoomFormal = Loadable({
  loader: () => import('./pages/waitingRoomFormal'),
  loading: Loading,
});

const WaitingRoomInformal = Loadable({
  loader: () => import('./pages/waitingRoomInformal'),
  loading: Loading,
});

const CheckInDetailInformal = Loadable({
  loader: () =>
    import('@modules/appeals/pages/waitingRoomInformal/CheckInDetailView'),
  loading: Loading,
});

const CheckInDetailFormal = Loadable({
  loader: () =>
    import('@modules/appeals/pages/waitingRoomFormal/CheckInDetailView'),
  loading: Loading,
});

const PanelOverview = Loadable({
  loader: () => import('./pages/panelOverview'),
  loading: Loading,
});

const PanelOverviewCheckInDetailInformal = Loadable({
  loader: () =>
    import('@modules/appeals/pages/panelOverview/CheckInDetailInformalView'),
  loading: Loading,
});

const PanelOverviewCheckInDetailFormal = Loadable({
  loader: () =>
    import('@modules/appeals/pages/panelOverview/CheckInDetailFormalView'),
  loading: Loading,
});

const ManagerOverview = Loadable({
  loader: () => import('./pages/managerOverview'),
  loading: Loading,
});

const AppraiserOverview = Loadable({
  loader: () => import('./pages/appraiserOverview'),
  loading: Loading,
});

const AppraiserOverviewCheckInDetailInformal = Loadable({
  loader: () =>
    import(
      '@modules/appeals/pages/appraiserOverview/CheckInDetailInformalView'
    ),
  loading: Loading,
});

const AppraiserOverviewCheckInDetailFormal = Loadable({
  loader: () =>
    import('@modules/appeals/pages/appraiserOverview/CheckInDetailFormalView'),
  loading: Loading,
});

const MeetingDetail = Loadable({
  loader: () => import('./pages/meeting'),
  loading: Loading,
});

const SelectedHearing = Loadable({
  loader: () => import('./pages/selectedHearing'),
  loading: Loading,
});

const AgentDocketGroupSearch = Loadable({
  loader: () => import('./pages/agentDocketGroups/search'),
  loading: Loading,
});

const TaxpayerPresentationView = Loadable({
  loader: () => import('./pages/meeting/TaxPayerPresentation'),
  loading: Loading,
});

const CadPresentationView = Loadable({
  loader: () => import('./pages/meeting/CadPresentation'),
  loading: Loading,
});

const PanelStatisticsView = Loadable({
  loader: () => import('./pages/panelStatistics'),
  loading: Loading,
});

const CopyFormalHearing = Loadable({
  loader: () => import('./pages/copyFormalHearing'),
  loading: Loading,
});

const AgentMassDownloadValidation = Loadable({
  loader: () => import('./pages/agentMassDownloadValidation'),
  loading: Loading,
});

export const appealsRoutes = {
  [AppealsRoutes.AppealsSearch]: {
    title: 'Appeals Search',
    component: (props) => <AppealsSearch {...props} />,
  },
  [AppealsRoutes.AppealSummary]: {
    title: 'Appeal Summary',
    component: (props) => <AppealSummary {...props} />,
  },
  [AppealsRoutes.AppealOwnerAgentPresentation]: {
    title: 'Appeal Owner/Agent Presentation',
    component: (props) => <AppealOwnerAgentPresentation {...props} />,
  },
  [AppealsRoutes.CreateAppealWalkthru]: {
    title: 'Create Appeal',
    component: (props) => <CreateAppealWalkthru {...props} />,
  },
  [AppealsRoutes.MassCreateAppeal]: {
    title: 'Mass Create Appeal',
    component: (props) => <MassCreateAppeal {...props} />,
  },
  [AppealsRoutes.ResetPrimaryProtester]: {
    title: 'Reset Primary Protestor',
    component: (props) => <ResetPrimaryProtester {...props} />,
  },
  [AppealsRoutes.AddAuthorizedAgentProtest]: {
    title: 'Add Authorized Agent',
    component: (props) => <AddAuthorizedAgentProtest {...props} />,
  },
  [AppealsRoutes.AppealSmartLists]: {
    title: 'Appeal Smart Lists',
    component: (props) => <AppealSmartLists {...props} />,
  },
  [AppealsRoutes.BoardMemberMaintenance]: {
    title: 'Board Member Maintenance',
    component: (props) => <BoardMemberMaintenance {...props} />,
  },
  [AppealsRoutes.PanelMaintenance]: {
    title: 'Panel Maintenance',
    component: (props) => <PanelMaintenance {...props} />,
  },
  [AppealsRoutes.Dashboard]: {
    title: 'Appeals Dashboard',
    component: (props) => <Dashboard {...props} />,
  },
  [AppealsRoutes.FinalLetterHistory]: {
    title: 'Final Letter History',
    component: (props) => <FinalLetterHistory {...props} />,
  },
  [AppealsRoutes.FinalLetterDetail]: {
    title: 'Final Letter Detail',
    component: (props) => <FinalLetterDetail {...props} />,
  },
  [AppealsRoutes.MassFinalizeHistory]: {
    title: 'Mass Finalize History',
    component: (props) => <MassFinalizeHistory {...props} />,
  },
  [AppealsRoutes.MassProcessHistory]: {
    title: 'Mass Process History',
    component: (props) => <MassProcessHistory {...props} />,
  },
  [AppealsRoutes.MassLetterHistory]: {
    title: 'Mass Letter History',
    component: (props) => <MassLetterHistory {...props} />,
  },
  [AppealsRoutes.MassAttachHistory]: {
    title: 'Mass Attach History',
    component: (props) => <MassAttachHistory {...props} />,
  },
  [AppealsRoutes.EvidenceUploadHistory]: {
    title: 'Evidence Upload History',
    component: (props) => <EvidenceUploadHistory {...props} />,
  },
  [AppealsRoutes.MassDownloadHistory]: {
    title: 'Mass Download History',
    component: (props) => <MassDownloadHistory {...props} />,
  },
  [AppealsRoutes.MassAoaHistory]: {
    title: 'Mass Aoa History',
    component: (props) => <MassAoaHistory {...props} />,
  },
  [AppealsRoutes.AgentDocketGroupSearch]: {
    title: 'Agent Docket Group Search',
    component: (props) => <AgentDocketGroupSearch {...props} />,
  },
  [AppealsRoutes.AgentMassDownloadValidation]: {
    title: 'Agent Mass Download Validation',
    component: (props) => <AgentMassDownloadValidation {...props} />,
  },
};

export const configurationRoutes = {
  [AppealsRoutes.StatusCodes]: {
    title: 'Status Codes',
    component: (props) => <StatusCodes {...props} />,
  },
  [AppealsRoutes.AppealConfigurations]: {
    title: 'Appeal Configurations',
    component: (props) => <AppealConfigurations {...props} />,
  },
  [AppealsRoutes.EletronicDeliveryNoticeSetting]: {
    title: 'Eletronic Delivery Notice Setting',
    component: (props) => <EletronicDeliveryNoticeSetting {...props} />,
  },
  [AppealsRoutes.AppealsCommercialConfiguration]: {
    title: 'Appeals Commercial Configuration',
    component: (props) => <AppealsCommercialConfiguration {...props} />,
  },
  [AppealsRoutes.AppealsCommercialComponents]: {
    title: 'Appeals Commercial Components',
    component: (props) => <AppealsCommercialComponents {...props} />,
  },
  [AppealsRoutes.AppealsCommercialArtifacts]: {
    title: 'Appeals Commercial Artifacts',
    component: (props) => <AppealsCommercialArtifacts {...props} />,
  },
};

export const docketRoutes = {
  [AppealsRoutes.DocketLocations]: {
    title: 'Docket Locations',
    component: (props) => <DocketLocations {...props} />,
  },
  [AppealsRoutes.DocketsSearch]: {
    title: 'Docket Search',
    component: (props) => <DocketsSearch {...props} />,
  },
};

export const meetingRoutes = {
  [AppealsRoutes.InformalMeetingImport]: {
    title: 'Informal Meeting Import',
    component: (props) => <InformalMeetingImport {...props} />,
  },
  [AppealsRoutes.FormalMeetingImport]: {
    title: 'Formal Meeting Import',
    component: (props) => <FormalMeetingImport {...props} />,
  },
  [AppealsRoutes.WaitingRoomFormal]: {
    title: 'Waiting Room Formal',
    component: (props) => <WaitingRoomFormal {...props} />,
  },
  [AppealsRoutes.WaitingRoomInformal]: {
    title: 'Waiting Room Informal',
    component: (props) => <WaitingRoomInformal {...props} />,
  },
  [AppealsRoutes.WaitingRoomInformalCheckIn]: {
    title: 'Waiting Room Informal',
    component: (props) => <CheckInDetailInformal {...props} />,
  },
  [AppealsRoutes.WaitingRoomFormalCheckIn]: {
    title: 'Waiting Room Formal',
    component: (props) => <CheckInDetailFormal {...props} />,
  },
  [AppealsRoutes.PanelOverview]: {
    title: 'Panel Overview',
    component: (props) => <PanelOverview {...props} />,
  },
  [AppealsRoutes.PanelOverviewInformalCheckIn]: {
    title: 'Panel Overview Informal',
    component: (props) => <PanelOverviewCheckInDetailInformal {...props} />,
  },
  [AppealsRoutes.PanelOverviewFormalCheckIn]: {
    title: 'Panel Overview Formal',
    component: (props) => <PanelOverviewCheckInDetailFormal {...props} />,
  },
  [AppealsRoutes.ManagerOverview]: {
    title: 'Manager Overview',
    component: (props) => <ManagerOverview {...props} />,
  },
  [AppealsRoutes.AppraiserOverview]: {
    title: 'Appraiser Overview',
    component: (props) => <AppraiserOverview {...props} />,
  },
  [AppealsRoutes.AppraiserOverviewInformalCheckIn]: {
    title: 'Appraiser Overview Informal',
    component: (props) => <AppraiserOverviewCheckInDetailInformal {...props} />,
  },
  [AppealsRoutes.AppraiserOverviewFormalCheckIn]: {
    title: 'Appraiser Overview Formal',
    component: (props) => <AppraiserOverviewCheckInDetailFormal {...props} />,
  },
  [AppealsRoutes.Meeting]: {
    title: 'Appeals Meeting',
    component: (props) => <MeetingDetail {...props} />,
  },
  [AppealsRoutes.SelectedHearing]: {
    title: 'Selected Hearing',
    component: (props) => <SelectedHearing {...props} />,
  },
  [AppealsRoutes.TaxpayerPresentationView]: {
    title: 'Tax Payer Evidence',
    component: (props) => <TaxpayerPresentationView {...props} />,
  },
  [AppealsRoutes.CadPresentationView]: {
    title: 'CAD Presentation',
    component: (props) => <CadPresentationView {...props} />,
  },
  [AppealsRoutes.PanelStatisticsView]: {
    title: 'Panel Statistics',
    component: (props) => <PanelStatisticsView {...props} />,
  },
  [AppealsRoutes.CopyFormalHearing]: {
    title: 'Copy Formal Hearing',
    component: (props) => <CopyFormalHearing {...props} />,
  },
};

export const pathToTitleAndComponent = {
  ...appealsRoutes,
  ...configurationRoutes,
  ...docketRoutes,
  ...meetingRoutes,
};

const Routes = (props) => (
  <PageWrapper {...props}>
    <ConferenceWrapper prefix={prefix}>
      <Switch>
        {Object.entries(pathToTitleAndComponent).map(
          ([path, { component }]) => (
            <EditableRoute exact key={path} path={path} component={component} />
          ),
        )}
        <Redirect to={AppealsRoutes.AppealsSearch} />
      </Switch>
    </ConferenceWrapper>
  </PageWrapper>
);

export default Routes;
